/**
 * Helper para tradução de objetos multilíngue
 * Usado para traduzir campos dinâmicos vindos do backend (survey, planos, etc)
 */

/**
 * Obtém o texto localizado de um objeto baseado no idioma atual
 * @param text - String simples ou objeto com traduções {en: "...", pt: "..."}
 * @param language - Idioma atual ('pt' ou 'en')
 * @returns String traduzida no idioma especificado
 */
export const getLocalizedText = (text: unknown, language: string = 'pt'): string => {
  // Se for string simples, retorna diretamente
  if (typeof text === 'string') {
    return text;
  }
  
  // Se não for um objeto ou for null, tenta converter para string
  if (typeof text !== 'object' || text === null) {
    return String(text || '');
  }
  
  // Se tiver propriedades de idioma, retorna a tradução
  // Prioriza: idioma solicitado > pt > en > vazio
  const textObj = text as Record<string, string>;
  if (textObj.en || textObj.pt) {
    return textObj[language] || textObj['pt'] || textObj['en'] || '';
  }
  
  // Se não encontrou tradução, retorna o objeto como string
  return String(text);
};

/**
 * Cria uma função de tradução específica para um idioma
 * @param language - Idioma atual ('pt' ou 'en')
 * @returns Função que traduz objetos para o idioma especificado
 */
export const createTranslator = (language: string) => {
  return (text: unknown): string => getLocalizedText(text, language);
};

/**
 * Traduz um array de objetos multilíngue
 * @param array - Array de objetos com traduções
 * @param language - Idioma atual ('pt' ou 'en')
 * @returns Array de strings traduzidas
 */
export const translateArray = (array: unknown[], language: string): string[] => {
  if (!Array.isArray(array)) {
    return [];
  }
  
  return array.map(item => getLocalizedText(item, language));
};

/**
 * Verifica se um valor é um campo traduzível (objeto com propriedades de idioma)
 * @param value - Valor a ser verificado
 * @returns true se for um objeto com pelo menos uma propriedade de idioma (en, pt, es, etc.)
 */
export const isTranslatableField = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  
  // Verifica se tem pelo menos uma propriedade que parece ser um código de idioma
  const commonLanguages = ['en', 'pt', 'es', 'fr', 'de', 'it', 'ja', 'zh', 'ko', 'ru', 'ar'];
  return Object.keys(value as Record<string, unknown>).some(key => commonLanguages.includes(key.toLowerCase()));
};

/**
 * Traduz recursivamente qualquer objeto, array ou valor primitivo
 * Processa toda a estrutura de dados e traduz campos que contenham traduções
 * 
 * @param data - Dados a serem traduzidos (objeto, array, string, etc.)
 * @param language - Idioma de destino ('pt', 'en', etc.)
 * @returns Estrutura de dados traduzida mantendo o formato original
 * 
 * @example
 * // Traduz um objeto simples
 * const input = { title: { en: "Hello", pt: "Olá" } };
 * translateDeep(input, 'pt'); // { title: "Olá" }
 * 
 * @example
 * // Traduz estruturas complexas
 * const agent = {
 *   name: "Agent",
 *   settings: {
 *     basic: {
 *       description: { en: "Description", pt: "Descrição" }
 *     },
 *     prompts: [
 *       { title: { en: "General", pt: "Geral" }, content: "..." }
 *     ]
 *   }
 * };
 * const translated = translateDeep(agent, 'pt');
 */
export const translateDeep = <T = unknown>(data: T, language: string = 'pt'): T => {
  // Caso seja null ou undefined, retorna como está
  if (data === null || data === undefined) {
    return data;
  }

  // Se for um campo traduzível (objeto com en, pt, etc.)
  if (isTranslatableField(data)) {
    return getLocalizedText(data, language) as T;
  }

  // Se for um array, processa cada item recursivamente
  if (Array.isArray(data)) {
    return data.map(item => translateDeep(item, language)) as T;
  }

  // Se for um objeto, processa cada propriedade recursivamente
  if (typeof data === 'object') {
    const translated: Record<string, unknown> = {};

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        translated[key] = translateDeep((data as Record<string, unknown>)[key], language);
      }
    }
    
    return translated as T;
  }

  // Para valores primitivos (string, number, boolean), retorna como está
  return data;
};

/**
 * Alias específico para traduzir agent settings
 * Usa a função genérica translateDeep por baixo dos panos
 * 
 * @param settings - Objeto de settings do agent
 * @param language - Idioma de destino
 * @returns Settings traduzidas
 */
export const translateAgentSettings = <T = unknown>(settings: T, language: string = 'pt'): T => {
  return translateDeep(settings, language);
};

/**
 * Alias específico para traduzir dados de planos
 * Usa a função genérica translateDeep por baixo dos panos
 * 
 * @param planData - Dados do plano
 * @param language - Idioma de destino
 * @returns Dados do plano traduzidos
 */
export const translatePlanData = <T = unknown>(planData: T, language: string = 'pt'): T => {
  return translateDeep(planData, language);
};

/**
 * Obtém a descrição de uma integração com suporte a i18n e retrocompatibilidade
 * Prioriza settings.description (traduzível) sobre description (campo legado)
 * @param integration - Objeto de integração (Integration ou IntegrationBase)
 * @param language - Idioma atual ('pt' ou 'en')
 * @returns Descrição traduzida ou null
 */
export const getIntegrationDescription = (
  integration: { description?: string | null; settings?: { description?: string | { en: string; pt: string } } | null } | null | undefined,
  language: string = 'pt'
): string | null => {
  // Guard: verificar se integration existe
  if (!integration) {
    return null;
  }
  
  // Prioridade 1: settings.description (novo formato traduzível)
  if (integration.settings?.description) {
    return getLocalizedText(integration.settings.description, language);
  }
  
  // Prioridade 2: description direta (formato legado para retrocompatibilidade)
  if (integration.description) {
    return integration.description;
  }
  
  return null;
};

// Alias para compatibilidade (deprecated - use getLocalizedText)
export const getTranslated = getLocalizedText;
